import VerboseLogModal from 'components/Debug/VerboseLogModal'
import MessageContextTypeSelector from 'components/Debug/MessageContextTypeSelector'
import MessageSearchTypeSelector from 'components/Debug/MessageSearchTypeSelector'
import MessageContextSizeSelector from 'components/Debug/MessageContextSizeSelector'
import RelevanceInputField from 'components/Debug/RelevanceInputField'
import TemperatureInputField from 'components/Debug/TemperatureInputField'
import MessageModelTypeSelector from 'components/Debug/MessageModelTypeSelector'
import MessageMaxContextSizeSelector from 'components/Debug/MessageMaxContextSizeSelector'
import MaxTemperatureInputField from 'components/Debug/MaxTemperatureInputField'
import DistanceInputField from 'components/Debug/DistanceInputField'
import CombineSearchSwitch from 'components/Debug/CombinedSearchSwitch'
import { Button, Stack } from '@mui/material'
import { Twin, TwinContextSource, TwinConversationType, TwinMessageContextType, TwinMessageSearchType } from 'api/model'
import ConversationTypeSelector from './ConversationTypeSelector'
import MapReduceDepthTypeSelector from './MapReduceTypeSelector'
import MobiusSwitch from './MobiusSwitch'
import SummaryModelTypeSelector from './SummaryModelTypeSelector'
import StreamingSwitch from './StreamingSwitch'
import { useState } from 'react'
import EmailIntentModelTypeSelector from './EmailIntentModelTypeSelector'

// validates debug settings
export const validateDebugSettings = (temperature: number, maxTemperature: number, distance: number) => {
  const isTempGreaterThanMaxTemp = temperature > maxTemperature
  const isTempTooHigh = temperature > 5
  const isMaxTempTooHigh = maxTemperature > 5
  const isDistanceTooHigh = distance > 1
  return !(isTempGreaterThanMaxTemp || isTempTooHigh || isMaxTempTooHigh || isDistanceTooHigh)
}

const DebugSettings = ({
  isSystemAdmin,
  twin,
  modelType,
  setModelType,
  emailIntentModelType,
  setEmailIntentModelType,
  temperature,
  setTemperature,
  maxTemperature,
  setMaxTemperature,
  relevance,
  setRelevance,
  contextType,
  setContextType,
  contextSize,
  setContextSize,
  maxContextSize,
  setMaxContextSize,
  searchType,
  setSearchType,
  distance,
  setDistance,
  combineSearch,
  setCombineSearch,
  verboseOutput,
  setIsVerboseLogModalOpen,
  isVerboseLogModalOpen,
  contextSources,
  conversationType,
  mapReduceDepth,
  setMapReduceDepth,
  setConversationType,
  isConversationSelectorDisabled,
  isDarkBackdrop,
  isConversationTypeHidden,
  summaryModelType,
  setSummaryModelType,
  isStreamingToggled,
  setIsStreamingToggled,
  isMobiusToggled,
  setIsMobiusToggled,
}: {
  isSystemAdmin: boolean
  twin?: Twin
  modelType: string
  setModelType: (modelType: string) => void
  summaryModelType: string
  setSummaryModelType: (summaryModelType: string) => void
  emailIntentModelType: string
  setEmailIntentModelType: (emailIntentModelType: string) => void
  temperature: number
  setTemperature: (temperature: number) => void
  maxTemperature: number
  setMaxTemperature: (maxTemperature: number) => void
  relevance: number
  setRelevance: (relevance: number) => void
  contextType: TwinMessageContextType
  setContextType: (contextType: TwinMessageContextType) => void
  contextSize: string
  setContextSize: (contextSize: string) => void
  maxContextSize: string
  setMaxContextSize: (maxContextSize: string) => void
  searchType: TwinMessageSearchType
  setSearchType: (searchType: TwinMessageSearchType) => void
  distance: number
  setDistance: (distance: number) => void
  combineSearch: boolean
  setCombineSearch: (combineSearch: boolean) => void
  verboseOutput?: string
  isVerboseLogModalOpen?: boolean
  setIsVerboseLogModalOpen?: (isVerboseLogModalOpen: boolean) => void
  mapReduceDepth: string
  setMapReduceDepth: (mapReduceDepth: string) => void
  contextSources?: TwinContextSource[]
  conversationType: TwinConversationType
  setConversationType: (conversationType: TwinConversationType) => void
  isConversationSelectorDisabled: boolean
  isDarkBackdrop: boolean // used to determine the label text color for debug fields
  isConversationTypeHidden?: boolean
  isStreamingToggled: boolean
  setIsStreamingToggled: React.Dispatch<React.SetStateAction<boolean>>
  isMobiusToggled: boolean
  setIsMobiusToggled: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  if (!isSystemAdmin) {
    return null
  }

  const onExpandClick = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <Stack>
      <Button
        onClick={onExpandClick}
        sx={{
          width: 'fit-content',
          mb: 2,
        }}>
        {isExpanded ? 'Hide debug settings' : 'Show debug settings'}
      </Button>
      {isExpanded && (
        <Stack mt={2} flexDirection="row" flexWrap="wrap" rowGap="48px" columnGap="24px">
          {!isConversationTypeHidden && (
            <ConversationTypeSelector
              isDarkBackdrop={isDarkBackdrop}
              disabled={isConversationSelectorDisabled}
              conversationType={conversationType}
              setConversationType={setConversationType}
            />
          )}
          {conversationType === TwinConversationType.chat && (
            <StreamingSwitch
              isDarkBackdrop={isDarkBackdrop}
              isStreamingToggled={isStreamingToggled}
              setIsStreamingToggled={setIsStreamingToggled}
            />
          )}
          {conversationType === TwinConversationType.chat && (
            <MobiusSwitch
              isDarkBackdrop={isDarkBackdrop}
              isMobiusToggled={isMobiusToggled}
              setIsMobiusToggled={setIsMobiusToggled}
            />
          )}
          {twin && (
            <MessageModelTypeSelector
              isDarkBackdrop={isDarkBackdrop}
              models={twin.models}
              modelType={modelType}
              setModelType={setModelType}
            />
          )}
          {twin && (
            <SummaryModelTypeSelector
              isDarkBackdrop={isDarkBackdrop}
              models={twin.models}
              summaryModelType={summaryModelType}
              setSummaryModelType={setSummaryModelType}
            />
          )}
          {twin && (
            <EmailIntentModelTypeSelector
              isDarkBackdrop={isDarkBackdrop}
              models={twin.models}
              emailIntentModelType={emailIntentModelType}
              setEmailIntentModelType={setEmailIntentModelType}
            />
          )}
          <TemperatureInputField
            isDarkBackdrop={isDarkBackdrop}
            maxTemperature={maxTemperature}
            temperature={temperature}
            setTemperature={setTemperature}
          />
          <MaxTemperatureInputField
            isDarkBackdrop={isDarkBackdrop}
            temperature={temperature}
            maxTemperature={maxTemperature}
            setMaxTemperature={setMaxTemperature}
          />
          <RelevanceInputField isDarkBackdrop={isDarkBackdrop} relevance={relevance} setRelevance={setRelevance} />
          <MessageContextTypeSelector
            isDarkBackdrop={isDarkBackdrop}
            contextType={contextType}
            setContextType={setContextType}
          />
          <MessageContextSizeSelector
            isDarkBackdrop={isDarkBackdrop}
            contextSize={contextSize}
            setContextSize={setContextSize}
          />
          <MessageMaxContextSizeSelector
            isDarkBackdrop={isDarkBackdrop}
            maxContextSize={maxContextSize}
            setMaxContextSize={setMaxContextSize}
          />
          <MessageSearchTypeSelector
            isDarkBackdrop={isDarkBackdrop}
            searchType={searchType}
            setSearchType={setSearchType}
          />
          <MapReduceDepthTypeSelector
            isDarkBackdrop={isDarkBackdrop}
            mapReduceDepth={mapReduceDepth}
            setMapReduceDepth={setMapReduceDepth}
          />
          <DistanceInputField isDarkBackdrop={isDarkBackdrop} distance={distance} setDistance={setDistance} />
          <CombineSearchSwitch
            isDarkBackdrop={isDarkBackdrop}
            combineSearch={combineSearch}
            setCombineSearch={setCombineSearch}
          />
          {verboseOutput && (
            <>
              <Button
                onClick={() => setIsVerboseLogModalOpen?.(true)}
                style={{
                  width: 'fit-content',
                  marginTop: '8px',
                }}>
                Open verbose log
              </Button>
              {isVerboseLogModalOpen && (
                <VerboseLogModal
                  contextSources={contextSources || []}
                  onClose={() => setIsVerboseLogModalOpen?.(false)}
                  verboseOutput={verboseOutput}
                />
              )}
            </>
          )}
        </Stack>
      )}
    </Stack>
  )
}

export default DebugSettings
